import {
  Box,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import {
  APPLICANT_STAGE_LABELS,
  APPLICANT_STAGES,
  REFERRAL_REPORTS_NOTE,
  trusteeName,
  type ReferralDashboard,
  type ReferralRate,
  type ReferralListFilters,
} from '@hv/shared';
import { BRAND } from '../theme';
import { referralsHref } from './filters';

export function DrillCount({
  count,
  href,
  description,
}: {
  count: number;
  href?: string;
  description: string;
}) {
  if (!count || !href) {
    return (
      <Box component="span" sx={{ color: count ? 'text.primary' : 'text.disabled' }}>
        {count}
      </Box>
    );
  }
  return (
    <Link component={RouterLink} to={href} fontWeight={700} aria-label={`${count} ${description}`}>
      {count}
    </Link>
  );
}

export function ReportsNote() {
  return (
    <Typography variant="body2" color="text.secondary" data-testid="tracking-note">
      {REFERRAL_REPORTS_NOTE} Drafts are never counted.
    </Typography>
  );
}

function StatCard({
  label,
  value,
  sub,
  href,
}: {
  label: string;
  value: number | string;
  sub?: string;
  href?: string;
}) {
  return (
    <Paper sx={{ p: 2.5 }} data-testid={`card-${label}`}>
      <Typography variant="h4" component="h3">
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: 32, fontWeight: 800, color: BRAND.navy, lineHeight: 1.2, mt: 0.5 }}
      >
        {href ? (
          <Link
            component={RouterLink}
            to={href}
            underline="hover"
            aria-label={`${label}: ${value}`}
          >
            {value}
          </Link>
        ) : (
          value
        )}
      </Typography>
      {sub && (
        <Typography variant="body2" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

export const plural = (count: number, one: string, many: string) => (count === 1 ? one : many);

const rateValue = (r: ReferralRate) => (r.percent === null ? '—' : `${r.percent}%`);

export function ReferralDashboardSection({
  data,
  cycleId,
}: {
  data: ReferralDashboard;
  cycleId: string;
}) {
  const href = (d: Omit<ReferralListFilters, 'cycleId'> = {}) => referralsHref({ cycleId, ...d });
  const children = data.childrenReferred;
  return (
    <Box component="section" aria-labelledby="referral-dashboard-title" sx={{ mt: 3 }}>
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <Typography id="referral-dashboard-title" variant="h2">
          Trustee referrals
        </Typography>
        <Link component={RouterLink} to={`/reports?cycle=${cycleId}`}>
          Open referral reports
        </Link>
      </Stack>
      <Box mb={2}>
        <ReportsNote />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 2,
        }}
      >
        <StatCard
          label="Referrals submitted"
          value={data.referralsSubmitted}
          href={data.referralsSubmitted ? href() : undefined}
        />
        <StatCard
          label="Trustee recommendations"
          value={data.recommendations}
          sub="Active recommendations"
        />
        <StatCard label="Families referred" value={data.familiesReferred} />
        <StatCard label="Children referred" value={children} />
        <StatCard
          label="Awaiting review"
          value={data.awaitingReview}
          sub="Status Submitted"
          href={data.awaitingReview ? href({ status: 'submitted' }) : undefined}
        />
        <StatCard
          label="Possible duplicates pending"
          value={data.duplicatesPending}
          sub="Referrals with an open duplicate check"
          href={data.duplicatesPending ? href({ duplicateStatus: 'open' }) : undefined}
        />
        <StatCard
          label="Referral-to-application rate"
          value={rateValue(data.applicationRate)}
          sub={`${data.applicationRate.count} of ${children} children at Application complete or later`}
        />
        <StatCard
          label="Enrollment rate"
          value={rateValue(data.enrollmentRate)}
          sub={`${data.enrollmentRate.count} of ${children} children enrolled`}
        />
      </Box>
      <TrusteeTable data={data} href={href} />
    </Box>
  );
}

function TrusteeTable({
  data,
  href,
}: {
  data: ReferralDashboard;
  href: (d: Omit<ReferralListFilters, 'cycleId'>) => string;
}) {
  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="h3" component="h3">
        Referrals by trustee
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Tracking only, not a ranking. Trustees are listed alphabetically; each count opens the
        matching referrals.
      </Typography>
      {data.trustees.length === 0 ? (
        <Typography>No submitted referrals in this cycle yet.</Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label="Referrals by trustee">
            <TableHead>
              <TableRow>
                <TableCell colSpan={4} />
                <TableCell
                  colSpan={APPLICANT_STAGES.length}
                  align="center"
                  sx={{ borderLeft: 1, borderColor: 'divider' }}
                >
                  Children by applicant stage
                </TableCell>
                {data.grades.length > 0 && (
                  <TableCell
                    colSpan={data.grades.length}
                    align="center"
                    sx={{ borderLeft: 1, borderColor: 'divider' }}
                  >
                    Children by grade
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell>Trustee</TableCell>
                <TableCell align="right">Referrals</TableCell>
                <TableCell align="right">Families</TableCell>
                <TableCell align="right">Children</TableCell>
                {APPLICANT_STAGES.map((s, i) => (
                  <TableCell
                    key={s}
                    align="right"
                    sx={i === 0 ? { borderLeft: 1, borderColor: 'divider' } : undefined}
                  >
                    {APPLICANT_STAGE_LABELS[s]}
                  </TableCell>
                ))}
                {data.grades.map((g, i) => (
                  <TableCell
                    key={g}
                    align="right"
                    sx={i === 0 ? { borderLeft: 1, borderColor: 'divider' } : undefined}
                  >
                    {g}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.trustees.map((t) => {
                const name = trusteeName(t.trustee);
                const mine = (d: Omit<ReferralListFilters, 'cycleId' | 'trusteeUserId'> = {}) =>
                  href({ trusteeUserId: t.trustee.id, ...d });
                return (
                  <TableRow key={t.trustee.id} data-testid="trustee-row">
                    <TableCell component="th" scope="row">
                      <Typography variant="body2" fontWeight={600}>
                        {name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t.trustee.email}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <DrillCount
                        count={t.referrals}
                        href={mine()}
                        description={`${plural(t.referrals, 'referral', 'referrals')} by ${name}`}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <DrillCount
                        count={t.families}
                        href={mine()}
                        description={`${plural(t.families, 'family', 'families')} referred by ${name}`}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <DrillCount
                        count={t.children}
                        href={mine()}
                        description={`${plural(t.children, 'child', 'children')} referred by ${name}`}
                      />
                    </TableCell>
                    {APPLICANT_STAGES.map((s, i) => (
                      <TableCell
                        key={s}
                        align="right"
                        sx={i === 0 ? { borderLeft: 1, borderColor: 'divider' } : undefined}
                      >
                        <DrillCount
                          count={t.byStage[s]}
                          href={mine({ stage: s })}
                          description={`${plural(t.byStage[s], 'child', 'children')} at ${APPLICANT_STAGE_LABELS[s]} referred by ${name}`}
                        />
                      </TableCell>
                    ))}
                    {data.grades.map((g, i) => (
                      <TableCell
                        key={g}
                        align="right"
                        sx={i === 0 ? { borderLeft: 1, borderColor: 'divider' } : undefined}
                      >
                        <DrillCount
                          count={t.byGrade[g] ?? 0}
                          href={mine({ grade: g })}
                          description={`${plural(t.byGrade[g] ?? 0, 'child', 'children')} in grade ${g} referred by ${name}`}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
